import { max, without } from 'lib/array/arrayUtils'
import createSelector from 'lib/web/createSelector'
import { isAbandonedCart, isHotLead, isRecentlyViewed } from 'lib/highIntentOffers'
import { isHotelType } from 'lib/offer/offerTypes'
import { detectOfferTypeFromId, isIdHotelType } from 'lib/offer/offerUtils'
import { isLereEnabled } from 'lib/lere/isLereEnabled'

export const getAllAbandonedCarts = createSelector(
  (state: App.State) => state.recommendations.highIntent.offers,
  (highIntentOffers) => highIntentOffers.filter(isAbandonedCart),
)

/**
 * Get latest AC
 */
export const getLatestAbandonedCart = createSelector(
  getAllAbandonedCarts,
  (carts) => {
    if (carts.length) {
      const latestCart = max(carts, cart => cart.creationTime)
      return latestCart as App.AbandonedCartRecommendation
    }
  },
)

/**
 * Note that the first abandoned cart is excluded when presenting the HI list
 */
export const getHighIntentOffers = createSelector(
  (state: App.State) => state.recommendations.highIntent.offers,
  (state: App.State) => state.offer.offerErrors,
  getLatestAbandonedCart,
  (offers, offerErrors, abandonedCart) => {
    if (!isLereEnabled()) { return [] }

    // get all offers that were loaded successfully
    let candidates = offers.filter(offer => !offerErrors[offer.offerId])
    // remove first AC
    candidates = abandonedCart ? without(candidates, abandonedCart) : candidates
    // remove unsupported HL (we only support hotel HL atm)
    candidates = candidates.filter(offer => !isHotLead(offer) || isHotelType(offer.offerType))
    return candidates
  },
)

export const getHighIntentOfferIds = createSelector(
  getHighIntentOffers,
  (highIntentOffers: App.HighIntentOffer[]) => highIntentOffers.map(offer => offer.offerId),
)

export const getHighIntentTourOffers = createSelector(
  getHighIntentOffers,
  (offers: App.HighIntentOffer[]) => offers
    .filter(offer => detectOfferTypeFromId(offer.offerId) === 'direct_tour'),
)

export const getHighIntentHotelOffers = createSelector(
  getHighIntentOffers,
  (offers:App.HighIntentOffer[]) => offers.filter(offer => isIdHotelType(offer.offerId)),
)

export const getHighIntentHotelOfferIds = createSelector(
  getHighIntentHotelOffers,
  (offers:App.HighIntentOffer[]) => offers.map(offer => offer.offerId),
)

export const getRecentlyViewedOffers = createSelector(
  getHighIntentHotelOffers,
  (offers: App.HighIntentOffer[]) => offers.filter(isRecentlyViewed),
)
